import React, { useEffect, useState } from "react";
import { api, uploadLogo, resolveAssetUrl } from "@/lib/api";
import { useProfiles } from "@/lib/profiles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Building2, Save, Plus, Upload, X } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", gst_number: "", address: "", email: "", phone: "", bank_details: "", logo_url: "" };

export default function Profiles() {
  const [profiles, setProfiles] = useState([]);
  const [drafts, setDrafts] = useState({}); // id -> form
  const [uploading, setUploading] = useState({});
  const { refresh: refreshActive } = useProfiles();

  const load = async () => {
    const { data } = await api.get("/profiles");
    setProfiles(data);
    const d = {};
    data.forEach((p) => { d[p.id] = { ...empty, ...p }; });
    setDrafts(d);
    refreshActive();
  };

  useEffect(() => { load(); }, []);

  const updateField = (id, key, val) => {
    setDrafts((d) => ({ ...d, [id]: { ...d[id], [key]: val } }));
  };

  const handleLogoUpload = async (id, file) => {
    return handleImageUpload(id, file, "logo_url", "/upload/logo");
  };
  const handleSigUpload = async (id, file) => {
    return handleImageUpload(id, file, "signature_url", "/upload/signature");
  };
  const handleImageUpload = async (id, file, field, endpoint) => {
    if (!file) return;
    setUploading((s) => ({ ...s, [id + field]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url;
      const draft = { ...drafts[id], [field]: url };
      await api.put(`/profiles/${id}`, {
        name: draft.name, gst_number: draft.gst_number, address: draft.address,
        email: draft.email, phone: draft.phone, bank_details: draft.bank_details,
        logo_url: draft.logo_url, signature_url: draft.signature_url,
      });
      toast.success(field === "logo_url" ? "Logo uploaded" : "Signature uploaded");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading((s) => ({ ...s, [id + field]: false }));
    }
  };

  const removeImage = async (id, field) => {
    const draft = { ...drafts[id], [field]: "" };
    await api.put(`/profiles/${id}`, {
      name: draft.name, gst_number: draft.gst_number, address: draft.address,
      email: draft.email, phone: draft.phone, bank_details: draft.bank_details,
      logo_url: draft.logo_url, signature_url: draft.signature_url,
    });
    toast.success("Removed");
    load();
  };
  const removeLogo = (id) => removeImage(id, "logo_url");

  const saveProfile = async (id) => {
    const { name, gst_number, address, email, phone, bank_details, logo_url, signature_url } = drafts[id];
    await api.put(`/profiles/${id}`, { name, gst_number, address, email, phone, bank_details, logo_url, signature_url });
    toast.success("Profile saved");
    load();
    refreshActive();
  };

  const addProfile = async () => {
    await api.post("/profiles", { name: `Business ${profiles.length + 1}`, gst_number: "", address: "" });
    toast.success("Business added"); load();
  };

  const fieldCls = "bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-[#D4AF37] text-white font-mont placeholder:text-neutral-600";

  return (
    <div className="px-10 py-10">
      <div className="flex items-end justify-between mb-12">
        <div>
          <p className="font-mont text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">— Identity</p>
          <h1 className="font-serif text-5xl tracking-tight text-white">Business Profiles</h1>
          <p className="text-neutral-500 text-sm mt-3 max-w-xl">Maintain up to two parallel business identities. Switch between them when issuing invoices.</p>
        </div>
        <Button data-testid="add-profile-btn" onClick={addProfile} className="rounded-none bg-white text-black hover:bg-neutral-200 px-6 py-5">
          <Plus className="w-4 h-4 mr-2" strokeWidth={2} /> Add Business
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/10 border border-white/10">
        {profiles.map((p, idx) => {
          const f = drafts[p.id] || empty;
          return (
            <div key={p.id} data-testid={`profile-card-${idx}`} className="bg-[#121212] p-8">
              <div className="flex items-center gap-3 mb-8">
                <Building2 className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                <span className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500">Profile #{idx + 1}</span>
              </div>

              <div className="space-y-5">
                {/* Logo Upload */}
                <div>
                  <Label className="font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500">Logo</Label>
                  <div className="mt-2 flex items-center gap-4">
                    {f.logo_url ? (
                      <div className="relative w-24 h-24 border border-white/10 bg-white/5 flex items-center justify-center overflow-hidden">
                        <img src={resolveAssetUrl(f.logo_url)} alt="" className="max-w-full max-h-full object-contain" />
                        <button
                          data-testid={`remove-logo-${idx}`}
                          onClick={() => removeLogo(p.id)}
                          className="absolute top-1 right-1 bg-black/70 hover:bg-red-500 text-white p-1"
                        >
                          <X className="w-3 h-3" strokeWidth={2} />
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 border border-dashed border-white/15 flex items-center justify-center text-neutral-600">
                        <Building2 className="w-6 h-6" strokeWidth={1.2} />
                      </div>
                    )}
                    <label className="cursor-pointer">
                      <input
                        data-testid={`upload-logo-${idx}`}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml,image/gif"
                        className="hidden"
                        onChange={(e) => handleLogoUpload(p.id, e.target.files?.[0])}
                      />
                      <span className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-xs font-mont uppercase tracking-[0.2em] text-neutral-300 hover:text-white hover:bg-white/5 transition-colors">
                        <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {uploading[p.id] ? "Uploading…" : f.logo_url ? "Replace" : "Upload Logo"}
                      </span>
                    </label>
                  </div>
                  <p className="font-mont text-[9px] text-neutral-600 mt-2">PNG, JPG, WEBP, SVG · max 5MB</p>
                </div>

                {/* Signature Upload */}
                <div>
                  <Label className="font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500">Signature</Label>
                  <div className="mt-2 flex items-center gap-4">
                    {f.signature_url ? (
                      <div className="relative w-40 h-24 border border-white/10 bg-white flex items-center justify-center overflow-hidden">
                        <img src={resolveAssetUrl(f.signature_url)} alt="" className="max-w-full max-h-full object-contain" />
                        <button
                          data-testid={`remove-signature-${idx}`}
                          onClick={() => removeImage(p.id, "signature_url")}
                          className="absolute top-1 right-1 bg-black/70 hover:bg-red-500 text-white p-1"
                        >
                          <X className="w-3 h-3" strokeWidth={2} />
                        </button>
                      </div>
                    ) : (
                      <div className="w-40 h-24 border border-dashed border-white/15 flex items-center justify-center text-neutral-600 text-[10px] font-mont uppercase tracking-[0.2em]">
                        No signature
                      </div>
                    )}
                    <label className="cursor-pointer">
                      <input
                        data-testid={`upload-signature-${idx}`}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml,image/gif"
                        className="hidden"
                        onChange={(e) => handleSigUpload(p.id, e.target.files?.[0])}
                      />
                      <span className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-xs font-mont uppercase tracking-[0.2em] text-neutral-300 hover:text-white hover:bg-white/5 transition-colors">
                        <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {uploading[p.id + "signature_url"] ? "Uploading…" : f.signature_url ? "Replace" : "Upload Signature"}
                      </span>
                    </label>
                  </div>
                  <p className="font-mont text-[9px] text-neutral-600 mt-2">Transparent PNG works best</p>
                </div>

                {[
                  ["Company Name", "name"],
                  ["GST Number", "gst_number"],
                  ["Email", "email"],
                  ["Phone", "phone"],
                ].map(([label, key]) => (
                  <div key={key}>
                    <Label className="font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500">{label}</Label>
                    <Input
                      data-testid={`profile-${idx}-${key}-input`}
                      value={f[key]}
                      onChange={(e) => updateField(p.id, key, e.target.value)}
                      className={fieldCls}
                    />
                  </div>
                ))}

                <div>
                  <Label className="font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500">Address</Label>
                  <Textarea
                    data-testid={`profile-${idx}-address-input`}
                    value={f.address}
                    onChange={(e) => updateField(p.id, "address", e.target.value)}
                    className="bg-transparent border border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-[#D4AF37] text-white font-mont mt-1"
                  />
                </div>

                <div>
                  <Label className="font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500">Bank Details</Label>
                  <Textarea
                    data-testid={`profile-${idx}-bank-input`}
                    value={f.bank_details}
                    onChange={(e) => updateField(p.id, "bank_details", e.target.value)}
                    className="bg-transparent border border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-[#D4AF37] text-white font-mont mt-1"
                  />
                </div>

                <Button
                  data-testid={`save-profile-${idx}-btn`}
                  onClick={() => saveProfile(p.id)}
                  className="w-full rounded-none bg-white text-black hover:bg-neutral-200 font-sans"
                >
                  <Save className="w-4 h-4 mr-2" strokeWidth={2} /> Save Profile
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
